// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Alert } from 'antd';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '../../utils/utils';
import CopyButton from '../copyButton/copyButton';
import './OneTimeSecretField.less';

interface OneTimeSecretFieldProps {
  value: string;
  masked: boolean;
  onToggleMasked: () => void;
  onCopied: () => void;
  warningText: React.ReactNode;
  ariaLabel?: string;
}

const OneTimeSecretField = ({
  value,
  masked,
  onToggleMasked,
  onCopied,
  warningText,
  ariaLabel,
}: OneTimeSecretFieldProps) => {
  const { t } = useTranslation();
  const hasFocus = useRef(false);

  // Detect OS copy shortcut while this input has focus. Uses a document-level
  // listener because the input is readOnly and browsers don't fire native copy
  // events on readOnly inputs; hasFocus.current gates it to this element only.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && hasFocus.current) {
        e.preventDefault();
        copyToClipboard(value);
        onCopied();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [value, onCopied]);

  const displayValue = masked ? '*'.repeat(value.length) : value;

  return (
    <div className="one-time-secret-field">
      <div className="one-time-secret-input-row">
        <input
          value={displayValue}
          readOnly
          className="one-time-secret-input"
          onFocus={() => { hasFocus.current = true; }}
          onBlur={() => { hasFocus.current = false; }}
        />
        <button
          type="button"
          className="one-time-secret-eye-btn"
          onClick={onToggleMasked}
          aria-label={masked ? t('common.one-time-secret.show') : t('common.one-time-secret.hide')}
        >
          {masked ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        <CopyButton
          textToCopy={value}
          dynamicLabel={{ initialLabel: '', labelAfterClick: '', dynamicLabelDuration: 2000 }}
          onClick={() => onCopied()}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        />
      </div>
      <Alert
        message={warningText}
        type="warning"
        showIcon
        icon={<AlertTriangle size={22} />}
        className="one-time-secret-warning"
      />
    </div>
  );
};

export default OneTimeSecretField;
